import { useRef, useState } from 'react';
import type { AdminUser } from '@baex/shared';
import { normalizeEmail, validateDisplayName, validateEmail } from '@baex/shared';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { TextField } from '../../components/ui/Field';
import { Modal } from '../../components/ui/Modal';
import { focusFirstInvalid } from '../../lib/focusFirstInvalid';
import {
  PASSWORD_MIN_LENGTH,
  validatePassword,
  validatePasswordConfirmation,
} from './passwordRules';

export const EMAIL_ALREADY_ADMIN = 'This email is already registered as an admin.';

/** Add Admin User — one modal, one step, with the password and confirm fields (see the "Admin Users — table and controls" section). */
export function AddAdminModal({
  open,
  existing,
  onCancel,
  onAdd,
}: {
  open: boolean;
  existing: AdminUser[];
  onCancel: () => void;
  onAdd: (input: { email: string; displayName: string; password: string }) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [displayNameError, setDisplayNameError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [confirmationError, setConfirmationError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const emailRef = useRef<HTMLInputElement | null>(null);
  const displayNameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const confirmationRef = useRef<HTMLInputElement | null>(null);

  function reset() {
    setEmail('');
    setDisplayName('');
    setPassword('');
    setConfirmation('');
    setEmailError(undefined);
    setDisplayNameError(undefined);
    setPasswordError(undefined);
    setConfirmationError(undefined);
    setSubmitError(null);
    setBusy(false);
  }

  function checkEmail(value: string): string | undefined {
    const result = validateEmail(value);
    if (!result.ok) return result.reason;
    // Case-insensitive match against active and disabled admins alike.
    const normalized = normalizeEmail(value);
    if (existing.some((admin) => normalizeEmail(admin.email) === normalized))
      return EMAIL_ALREADY_ADMIN;
    return undefined;
  }

  function checkDisplayName(value: string): string | undefined {
    const result = validateDisplayName(value);
    return result.ok ? undefined : result.reason;
  }

  async function submit() {
    // Everything is re-checked, blurred or not, so an untouched empty form still says
    // what is wrong with it (see the "Admin Interface" section, "A control never refuses in silence").
    const nextEmailError = checkEmail(email);
    const nextDisplayNameError = checkDisplayName(displayName);
    const nextPasswordError = validatePassword(password);
    const nextConfirmationError = validatePasswordConfirmation(password, confirmation);
    setEmailError(nextEmailError);
    setDisplayNameError(nextDisplayNameError);
    setPasswordError(nextPasswordError);
    setConfirmationError(nextConfirmationError);
    const atFault = focusFirstInvalid([
      { error: nextEmailError, ref: emailRef },
      { error: nextDisplayNameError, ref: displayNameRef },
      { error: nextPasswordError, ref: passwordRef },
      { error: nextConfirmationError, ref: confirmationRef },
    ]);
    if (atFault) return;

    setBusy(true);
    setSubmitError(null);
    try {
      await onAdd({ email: normalizeEmail(email), displayName: displayName.trim(), password });
      reset();
    } catch (error) {
      setSubmitError(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Add Admin User"
      onClose={() => {
        reset();
        onCancel();
      }}
      footer={
        <>
          <Button
            onClick={() => {
              reset();
              onCancel();
            }}
          >
            Cancel
          </Button>
          {/* Disabled only while its own request runs (see the "Admin Interface" section). */}
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          ref={emailRef}
          label="Email"
          type="email"
          value={email}
          error={emailError}
          onChange={(event) => {
            setEmail(event.target.value);
            if (emailError) setEmailError(checkEmail(event.target.value));
          }}
          onBlur={(event) => setEmailError(checkEmail(event.target.value))}
        />
        <TextField
          ref={displayNameRef}
          label="Display name"
          value={displayName}
          error={displayNameError}
          onChange={(event) => {
            setDisplayName(event.target.value);
            if (displayNameError) setDisplayNameError(checkDisplayName(event.target.value));
          }}
          onBlur={(event) => setDisplayNameError(checkDisplayName(event.target.value))}
        />
        <TextField
          ref={passwordRef}
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          error={passwordError}
          // Trimmed: the old third clause ("the admin can change it later by having it
          // reset") described the system's mechanics rather than anything the reader does
          // here, and read as though the new admin could change it themselves — they
          // cannot; there is no self-service reset in this console (see the "Admin Login" section).
          hint={`At least ${PASSWORD_MIN_LENGTH} characters. Hand it over through a secure channel.`}
          onChange={(event) => {
            setPassword(event.target.value);
            if (passwordError) setPasswordError(validatePassword(event.target.value));
            // Re-check the confirmation against the new value, so correcting the password
            // clears a mismatch instead of leaving a message that is no longer true.
            if (confirmationError)
              setConfirmationError(validatePasswordConfirmation(event.target.value, confirmation));
          }}
          onBlur={(event) => setPasswordError(validatePassword(event.target.value))}
        />
        <TextField
          ref={confirmationRef}
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirmation}
          error={confirmationError}
          onChange={(event) => {
            setConfirmation(event.target.value);
            if (confirmationError)
              setConfirmationError(validatePasswordConfirmation(password, event.target.value));
          }}
          onBlur={(event) =>
            setConfirmationError(validatePasswordConfirmation(password, event.target.value))
          }
        />

        {submitError ? (
          <ErrorNotice error={submitError} fallback="The admin user could not be added." />
        ) : null}
      </div>
    </Modal>
  );
}
